import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

function todayDayKey() {
  const jsDay = new Date().getDay(); // 0=Sun,1=Mon...6=Sat
  return DAYS[jsDay === 0 ? 6 : jsDay - 1];
}

function isWithinSchedule(sched) {
  const d = sched?.[todayDayKey()];
  if (!d) return false;
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  const [oh, om] = d.open.split(':').map(Number);
  const [ch, cm] = d.close.split(':').map(Number);
  return cur >= oh * 60 + om && cur < ch * 60 + cm;
}

function fmtSchedHint(sched) {
  const d = sched?.[todayDayKey()];
  if (!d) return sched && Object.keys(sched).length ? 'Closed today' : '';
  return `Today: ${d.open}–${d.close}`;
}

const StaffPage = ({ restaurantId }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('restaurants')
        .select('store_settings')
        .eq('id', restaurantId)
        .maybeSingle();
      if (fetchError) throw fetchError;
      setSettings(data?.store_settings || {});
    } catch (err) {
      setError(err.message || 'Failed to load store status.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (field, updates) => {
    const next = { ...settings, ...updates };
    setSettings(next);
    setSaving(field);
    setError('');
    setNotice('');
    try {
      const { error: patchError } = await supabase
        .from('restaurants')
        .update({ store_settings: next })
        .eq('id', restaurantId);
      if (patchError) throw patchError;
      setNotice('Saved.');
      setTimeout(() => setNotice(''), 2500);
    } catch (err) {
      setError(err.message || 'Failed to save.');
      load();
    } finally {
      setSaving(null);
    }
  };

  const toggleKiosk = () => {
    const nowOpen = settings?.kioskOpen !== false;
    const sched = settings?.kioskSchedule;
    const schedSaysOpen = sched ? isWithinSchedule(sched) : null;
    const updates = { kioskOpen: !nowOpen };
    if (schedSaysOpen !== null && !nowOpen === schedSaysOpen) {
      delete updates.kioskManualOverrideDate;
    } else {
      updates.kioskManualOverrideDate = new Date().toDateString();
    }
    patch('kioskOpen', updates);
  };

  const toggleOnline = () => {
    const nowOpen = settings?.onlineOrdersOpen !== false;
    const sched = settings?.onlineSchedule;
    const schedSaysOpen = sched ? isWithinSchedule(sched) : null;
    const updates = { onlineOrdersOpen: !nowOpen };
    if (schedSaysOpen !== null && !nowOpen === schedSaysOpen) {
      delete updates.onlineManualOverrideDate;
    } else {
      updates.onlineManualOverrideDate = new Date().toDateString();
    }
    patch('onlineOrdersOpen', updates);
  };

  if (loading) return <div className="staff-page-state">Loading store status...</div>;

  const kioskOpen = settings?.kioskOpen !== false;
  const onlineOpen = settings?.onlineOrdersOpen !== false;
  const kioskSched = settings?.kioskSchedule;
  const onlineSched = settings?.onlineSchedule;
  const today = new Date().toDateString();
  const hasSchedule = kioskSched || onlineSched;

  return (
    <div className="staff-page">
      <div className="staff-page-header">
        <h1>Staff</h1>
        <p>Manage store availability and review opening hours.</p>
      </div>

      {error && <div className="staff-notice staff-notice-error">{error}</div>}
      {notice && <div className="staff-notice staff-notice-success">{notice}</div>}

      <div className="staff-sections">
        <section className="staff-card">
          <h2>Store Status</h2>

          <div className="staff-status-row">
            <div className="staff-status-left">
              <span className="staff-status-label">Kiosk</span>
              {fmtSchedHint(kioskSched) && (
                <span className="staff-sched-hint">{fmtSchedHint(kioskSched)}</span>
              )}
              {settings?.kioskManualOverrideDate === today && (
                <span className="staff-override-tag">Manual override active</span>
              )}
            </div>
            <span className={`staff-badge ${kioskOpen ? 'open' : 'closed'}`}>
              {kioskOpen ? 'Open' : 'Closed'}
            </span>
            <button
              className={`staff-toggle-btn ${kioskOpen ? 'toggle-close' : 'toggle-open'}`}
              onClick={toggleKiosk}
              disabled={saving === 'kioskOpen'}
            >
              {kioskOpen ? 'Close Kiosk' : 'Open Kiosk'}
            </button>
          </div>

          <div className="staff-row-divider" />

          <div className="staff-status-row">
            <div className="staff-status-left">
              <span className="staff-status-label">Online Orders</span>
              {fmtSchedHint(onlineSched) && (
                <span className="staff-sched-hint">{fmtSchedHint(onlineSched)}</span>
              )}
              {settings?.onlineManualOverrideDate === today && (
                <span className="staff-override-tag">Manual override active</span>
              )}
            </div>
            <span className={`staff-badge ${onlineOpen ? 'open' : 'closed'}`}>
              {onlineOpen ? 'Open' : 'Closed'}
            </span>
            <button
              className={`staff-toggle-btn ${onlineOpen ? 'toggle-close' : 'toggle-open'}`}
              onClick={toggleOnline}
              disabled={saving === 'onlineOrdersOpen'}
            >
              {onlineOpen ? 'Stop Orders' : 'Accept Orders'}
            </button>
          </div>
        </section>

        {hasSchedule && (
          <section className="staff-card">
            <h2>Opening Hours</h2>
            <table className="staff-hours-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Kiosk</th>
                  <th>Online</th>
                </tr>
              </thead>
              <tbody>
                {DAYS.map(day => {
                  const kd = kioskSched?.[day];
                  const od = onlineSched?.[day];
                  const isToday = day === todayDayKey();
                  const kTxt = kd === null ? 'Closed' : kd ? `${kd.open}–${kd.close}` : '—';
                  const oTxt = od === null ? 'Closed' : od ? `${od.open}–${od.close}` : '—';
                  return (
                    <tr key={day} className={isToday ? 'staff-today' : ''}>
                      <td>{DAY_LABELS[day]}{isToday ? ' ◀' : ''}</td>
                      <td>{kTxt}</td>
                      <td>{oTxt}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="staff-hours-hint">To edit the schedule, go to Store Settings.</p>
          </section>
        )}
      </div>
    </div>
  );
};

export default StaffPage;
